import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

// Estado único de búsqueda (texto, nodos, fecha, orden, sección) en la URL de /buscar
// (OTT-1966 Fase 2, contrato sección 4). La URL es la única fuente de verdad: no hay
// estado duplicado en memoria, así que el botón atrás/adelante del navegador (popstate)
// relanza la consulta gratis en cuanto react-router re-renderiza con la nueva `location`.

export const SORT_KEYS = { RECENT: "recent", OLDEST: "oldest", AZ: "az" };

// Supuesto (Fase 2): el contrato propone `sport=` como nombre de parámetro para los nodos
// de filtro, pero es el ejemplo de ESTE tenant (deportes); el modal de filtros es genérico
// por diseño (ver pantalla 17-B, misma OTT con clubes/canciones). Se usa `nodes=` (igual que
// el parámetro de la API, en slugs) para no acoplar la URL compartida a un dominio concreto.
const PARAM = { TEXT: "q", NODES: "nodes", FROM: "from", TO: "to", SORT: "sort", SECTION: "section" };

const DEFAULT_STATE = {
  text: "",
  nodeSlugs: [],
  from: "",
  to: "",
  sortKey: SORT_KEYS.RECENT,
  sectionSlug: "",
};

export const parseSearchUrlState = (search) => {
  const params = new URLSearchParams(search);
  const nodes = params.get(PARAM.NODES);
  const sortKey = params.get(PARAM.SORT);
  return {
    text: params.get(PARAM.TEXT) || "",
    nodeSlugs: nodes ? nodes.split(",").filter(Boolean) : [],
    from: params.get(PARAM.FROM) || "",
    to: params.get(PARAM.TO) || "",
    sortKey: Object.values(SORT_KEYS).includes(sortKey) ? sortKey : SORT_KEYS.RECENT,
    sectionSlug: params.get(PARAM.SECTION) || "",
  };
};

export const serializeSearchUrlState = (state) => {
  const params = new URLSearchParams();
  if (state.text) params.set(PARAM.TEXT, state.text);
  if (state.nodeSlugs?.length) params.set(PARAM.NODES, state.nodeSlugs.join(","));
  if (state.from) params.set(PARAM.FROM, state.from);
  if (state.to) params.set(PARAM.TO, state.to);
  if (state.sortKey && state.sortKey !== SORT_KEYS.RECENT) params.set(PARAM.SORT, state.sortKey);
  if (state.sectionSlug) params.set(PARAM.SECTION, state.sectionSlug);
  return params.toString();
};

/**
 * Lee/escribe el estado de búsqueda directamente en la URL de la página actual.
 * @returns {{state: object, setText: Function, setNodeSlugs: Function, setDateRange: Function,
 *   setSortKey: Function, enterSection: Function, exitSection: Function, clearFilters: Function,
 *   clearAll: Function, pushState: Function}}
 */
export const useSearchUrlState = () => {
  const history = useHistory();
  const location = useLocation();

  const state = useMemo(() => parseSearchUrlState(location.search), [location.search]);

  const pushState = useCallback(
    (patch) => {
      const next = { ...state, ...patch };
      const search = serializeSearchUrlState(next);
      history.push({ pathname: location.pathname, search: search ? `?${search}` : "" });
    },
    [state, history, location.pathname],
  );

  const setText = useCallback((text) => pushState({ text }), [pushState]);
  const setNodeSlugs = useCallback((nodeSlugs) => pushState({ nodeSlugs }), [pushState]);
  // Fecha concreta: from === to (contrato §2.4). Vaciar un campo = quitarlo (sin límite en ese lado).
  const setDateRange = useCallback((from, to) => pushState({ from: from || "", to: to || "" }), [pushState]);
  const setSortKey = useCallback((sortKey) => pushState({ sortKey: sortKey || SORT_KEYS.RECENT }), [pushState]);
  // "Ver más" de una sección: se hereda el resto del estado tal cual (contrato §3/§6).
  const enterSection = useCallback((sectionSlug) => pushState({ sectionSlug }), [pushState]);
  // "‹ Volver" o atrás: se quita solo `section`, el resto del estado vigente se conserva.
  const exitSection = useCallback(() => pushState({ sectionSlug: "" }), [pushState]);
  // Chip ×/"Limpiar filtros": quita nodos y fecha; el texto se conserva (contrato §3).
  const clearFilters = useCallback(() => pushState({ nodeSlugs: [], from: "", to: "" }), [pushState]);
  // "Limpiar búsqueda y filtros" del estado vacío: vuelve al estado inicial (pantallas 11/16).
  const clearAll = useCallback(
    () => history.push({ pathname: location.pathname, search: "" }),
    [history, location.pathname],
  );

  return {
    state,
    setText,
    setNodeSlugs,
    setDateRange,
    setSortKey,
    enterSection,
    exitSection,
    clearFilters,
    clearAll,
    // Expuesto para "Aplicar filtros" (modal): nodos y fecha se pueden cambiar a la vez (desmarcar
    // la fecha y marcar un nodo nuevo en la misma pulsación). Dos llamadas seguidas a setNodeSlugs
    // + setDateRange no sirven aquí: cada una parte del mismo `state` capturado en este render, así
    // que la segunda pisaría el cambio de la primera con datos ya desactualizados. Con un único
    // pushState, ambos cambios se combinan en la misma URL.
    pushState,
  };
};

export const DEFAULT_SEARCH_URL_STATE = DEFAULT_STATE;
